/**
 * PDFReceiptPreview — F-008 Phase 6 (IMPL-P6-003).
 *
 * Anteprima della ricevuta caricata: iframe puntato sulla presigned GET URL
 * ottenuta via `useRicevutaPdfUrl`. Mirror di `PDFPreviewPane` (F-007) ma per
 * la ricevuta anziché per il PDF originale del contratto.
 *
 * Stati:
 *  - `s3Key` null/undefined → ritorna `null` (early return: niente da mostrare
 *    finché la PUT non è completata)
 *  - `isLoading` → skeleton `role="status"` + animate-pulse SOLO se l'utente
 *    NON preferisce reduced-motion (`prefers-reduced-motion: reduce`)
 *  - `error` → banner `role="alert"` con messaggio neutro
 *  - success → `<iframe>` con `title` accessibile
 *
 * Accessibilità:
 *  - `role="status"` + `aria-label` sullo skeleton
 *  - `role="alert"` sul fallback errore
 *  - `title` sull'iframe (richiesto WCAG 4.1.2)
 *  - `aria-label` esteso descrive il contenuto
 */
import { useEffect, useState } from 'react';
import { useRicevutaPdfUrl } from '../hooks/useRicevutaPdfUrl';

export interface PDFReceiptPreviewProps {
  praticaId: string;
  /** Chiave S3 della ricevuta. `null` finché l'upload non è completato. */
  s3Key: string | null;
  className?: string;
}

/**
 * Hook locale per `prefers-reduced-motion`. Sottoscrive il `MediaQueryList`
 * così le preferenze cambiate live (System Settings) sono rispettate.
 * Default `false` in environment senza `matchMedia` (jsdom test).
 */
function usePrefersReducedMotion(): boolean {
  const [prefers, setPrefers] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e: MediaQueryListEvent) => setPrefers(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  return prefers;
}

export function PDFReceiptPreview({
  praticaId,
  s3Key,
  className,
}: PDFReceiptPreviewProps) {
  const { data, isLoading, error } = useRicevutaPdfUrl(praticaId, s3Key);
  const prefersReducedMotion = usePrefersReducedMotion();

  if (!s3Key) return null;

  const wrapperClass =
    'w-full h-64 rounded-md border bg-muted/20' +
    (className ? ` ${className}` : '');

  if (isLoading) {
    const animClass = prefersReducedMotion ? '' : ' animate-pulse';
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Caricamento anteprima ricevuta PDF"
        className={`${wrapperClass}${animClass}`}
      />
    );
  }

  if (error || !data?.url) {
    return (
      <div
        role="alert"
        className={`${wrapperClass} flex items-center justify-center`}
      >
        <p className="text-sm text-muted-foreground">
          Anteprima ricevuta non disponibile
        </p>
      </div>
    );
  }

  return (
    <iframe
      src={data.url}
      title="Anteprima ricevuta PDF"
      aria-label="Anteprima della ricevuta di registrazione caricata"
      className={`${wrapperClass} border-0`}
    />
  );
}
